import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import ProtectedLayout from './components/ProtectedLayout';
import ScrollToTop from './components/common/ScrollToTop';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { UiSettingsProvider } from './contexts/UiSettingsContext';
import './styles/theme.css';
import './styles/settings.css';
import Layout from './components/Layout/Layout';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { GlobalErrorBoundary } from './components/common/GlobalErrorBoundary';
import EnvironmentIndicator from './components/common/EnvironmentIndicator';
import LandingPage from './pages/LandingPage';
import SecurityCompliance from './pages/SecurityCompliance';
import SecurityDashboard from './pages/SecurityDashboard';
import Contact from './pages/Contact';
import ContactThankYou from './pages/ContactThankYou';
import AboutEUnify from './pages/AboutEUnify';
import Capabilities from './pages/Capabilities';
import SearchResults from './pages/SearchResults';
import ContactInquiriesReport from './pages/admin/ContactInquiriesReport';
import AdminUserManagement from './pages/admin/UserManagement';
import Dashboard from './pages/Dashboard';
import DataCatalog from './pages/DataCatalog';
import DataAssetDetail from './pages/DataAssetDetail';
import DataGovernance from './pages/DataGovernance';
import Analytics from './pages/Analytics';
import Integration from './pages/Integration';
import DataCatalogFeature from './pages/features/DataCatalog';
import HighPerformance from './pages/capabilities/HighPerformance';
import ZeroTrustSecurity from './pages/capabilities/ZeroTrustSecurity';
import FedRAMPReady from './pages/capabilities/FedRAMPReady';
import Section508Compliant from './pages/capabilities/Section508Compliant';
import ScalableArchitecture from './pages/capabilities/ScalableArchitecture';
import NoVendorLockIn from './pages/capabilities/NoVendorLockIn';
import DataGovernanceFeature from './pages/features/DataGovernance';
import AnalyticsFeature from './pages/features/Analytics';
import IntegrationFeature from './pages/features/Integration';
import BusinessGlossaryFeature from './pages/features/BusinessGlossaryFeature';
import DataCatalogDetailFeature from './pages/features/DataCatalogDetailFeature';
import DataLineageFeature from './pages/features/DataLineageFeature';
import PolicyManagementFeature from './pages/features/PolicyManagementFeature';
import WorkflowAutomationFeature from './pages/features/WorkflowAutomationFeature';
import ImpactAnalysisFeature from './pages/features/ImpactAnalysisFeature';
import Learn101 from './pages/Learn101';
import DataLiteracyModule from './pages/DataLiteracyModule';
import NationalProductionDataset from './pages/NationalProductionDataset';
import E22Classification from './pages/E22Classification';
import DGvsMDMLesson from './components/Learn/DGvsMDMLesson';
import DataStewardLesson from './components/Learn/DataStewardLesson';
import AssetTypes from './pages/AssetTypes';
import PolicyManager from './pages/PolicyManager';
import DataAssetDemo from './pages/DataAssetDemo';
import BusinessProcesses from './pages/BusinessProcesses';
import DataCategories from './pages/DataCategories';
import DataConcepts from './pages/DataConcepts';
import DataDomains from './pages/DataDomains';
import SubjectCategories from './pages/SubjectCategories';
import LineOfBusiness from './pages/LineOfBusiness';
import BusinessTerms from './pages/BusinessTerms';
import BusinessGlossary from './pages/BusinessTerms';
import BusinessGlossaryTraining from './pages/BusinessGlossaryTraining';
import BusinessGlossaryTrainingV2 from './pages/BusinessGlossaryTrainingV2';
import BusinessGlossaryCatalog from './components/DataCatalog/BusinessGlossaryCatalog';
import DataIntelligenceTraining from './pages/DataIntelligenceTraining';
import DataLiteracyTraining from './pages/DataLiteracyTraining';
import DataGovernanceTraining from './pages/DataGovernanceTraining';
import DataCatalogTraining from './pages/DataCatalogTraining';
import DataQualityLineageTraining from './pages/DataQualityLineageTraining';
import VanguardCertification from './pages/VanguardCertification';
import Acronyms from './pages/Acronyms';
import BenefitsPage from './pages/BenefitsPage';
import BenefitDetail from './pages/BenefitDetail';

// Data Governance Pages
import GovernanceDashboard from './pages/governance/GovernanceDashboard';
import DataGovernanceCouncil from './pages/governance/DataGovernanceCouncil';
import PoliciesStandards from './pages/governance/PoliciesStandards';
import DataQualityMgmt from './pages/governance/DataQuality';
import ComplianceAssessments from './pages/governance/ComplianceAssessments';
import GovernanceHelpdesk from './pages/governance/GovernanceHelpdesk';

// Data Strategy Pages
import VisionMission from './pages/strategy/VisionMission';
import StrategicRoadmap from './pages/strategy/StrategicRoadmap';
import MaturityAssessment from './pages/strategy/MaturityAssessment';
import TrainingResources from './pages/strategy/TrainingResources';
import Reports from './pages/Reports';
import BIReports from './pages/BIReports';
import Help from './pages/Help';
import DomainTypes from './pages/admin/DomainTypes';
import Statuses from './pages/admin/Statuses';
import Characteristics from './pages/admin/Characteristics';
import Workflows from './pages/admin/Workflows';
import Roles from './pages/access/Roles';
import Permissions from './pages/access/Permissions';
import Profile from './pages/Profile';
import Jurisdictions from './pages/access/Jurisdictions';
import Compliance from './pages/Compliance';
import Settings from './pages/Settings';
import About from './pages/About';
import TestPage from './pages/TestPage';
import DataStrategySupport from './pages/DataStrategySupport';
import ProjectCharter from './pages/ProjectCharter';
import KpiDictionary from './pages/KpiDictionary';
import KpisBrowser from './pages/KpisBrowser';
import ProcessingCategories from './pages/processing/ProcessingCategories';
import ProcessingMeasures from './pages/processing/ProcessingMeasures';
import ProcessingMigration from './pages/processing/ProcessingMigration';
import GDPRPolicy from './pages/policy/GDPRPolicy';
import PolicyIndex from './pages/policy/PolicyIndex';
import Standards from './pages/policy/Standards';
import StandardsLibrary from './pages/StandardsLibrary';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import DevLogin from './pages/DevLogin';
import UnidatumProjectManagerPlanningV2 from './pages/dev/UnidatumProjectManagerPlanningV2';
import PDCADashboard from './pages/dev/PDCADashboard';
import StudyAidsBusinessAnalytics from './pages/StudyAidsBusinessAnalytics';
import WeeklyStatusList from './pages/WeeklyStatusList';
import MonthlyStatusList from './pages/MonthlyStatusList';
import DataGovernanceQuality from './pages/DataGovernanceQuality';
import DataQuality from './pages/DataQuality';
import UserManagement from './pages/UserManagement';
import DataLineage from './pages/DataLineage';
import DataStewardCenter from './pages/DataStewardCenter';
import AdminMenuManagement from './pages/AdminMenuManagement';
import DataStrategyPlanning from './pages/DataStrategyPlanning';
import DataCatalogRoadmap from './pages/DataCatalogRoadmap';
import AIGovernanceRoadmap from './pages/AIGovernanceRoadmap';
import PortfolioManagementRoadmap from './pages/PortfolioManagementRoadmap';
import NPDRoadmap from './pages/NPDRoadmap';
import TeamRoster from './pages/TeamRoster';
import TeamRosterPicklistManagement from './pages/TeamRosterPicklistManagement';
import JanusGraphVisualization from './pages/JanusGraphVisualization';
import OpenTasks from './pages/OpenTasks';
import FederalDataStrategy from './pages/FederalDataStrategy';
import USCISApplicationTracking from './pages/USCISApplicationTracking';
import DataStrategyOperationsCenter from './pages/DataStrategyOperationsCenter';
import ProjectDetails from './pages/ProjectDetails';
import TeamUtilizationDetails from './pages/TeamUtilizationDetails';
import ComplianceDetails from './pages/ComplianceDetails';
import AutomatedProcesses from './pages/AutomatedProcesses';
import ScheduledProcessesPage from './pages/ScheduledProcessesPage';
import ProcessMonitoring from './components/ProcessMonitoring';
import Portfolio from './pages/Portfolio';

import { DocumentationPage } from './pages/DocumentationPage';
import DocumentationCenter from './pages/DocumentationCenter';
import DocumentationCombined from './pages/DocumentationCombined';
import CapstoneProjects from './pages/CapstoneProjects';
import DocsViewer from './pages/DocsViewer';
import CapstoneProjectDetail from './pages/CapstoneProjectDetail';
import CaseStudyMAF from './pages/CaseStudyMAF';
import DataInventoryTimeline from './components/DataInventoryTimeline';
import DataStrategyModules from './pages/DataStrategyModules';
import DataStrategyModuleDetail from './pages/DataStrategyModuleDetail';
import PIPlanningBoard from './pages/PIPlanningBoard';
import ATSSystemDetail from './pages/ATSSystemDetail';
import IFTRVSSSystemDetail from './pages/IFTRVSSSystemDetail';
import EIDSystemDetail from './pages/EIDSystemDetail';
import UIPSystemDetail from './pages/UIPSystemDetail';
import ACESystemDetail from './pages/ACESystemDetail';
import ABISystemDetail from './pages/ABISystemDetail';
import CSMSSystemDetail from './pages/CSMSSystemDetail';
import AAPSystemDetail from './pages/AAPSystemDetail';
import AILLMSystemDetail from './pages/AILLMSystemDetail';
import CloudEdgeSystemDetail from './pages/CloudEdgeSystemDetail';
import OITPortalSystemDetail from './pages/OITPortalSystemDetail';
import InnovationHubSystemDetail from './pages/InnovationHubSystemDetail';
import ATSCargoSystemDetail from './pages/ATSCargoSystemDetail';
import ATSPassengerSystemDetail from './pages/ATSPassengerSystemDetail';
import ATSLandSystemDetail from './pages/ATSLandSystemDetail';
import ATSInternationalSystemDetail from './pages/ATSInternationalSystemDetail';
import ATSGlobalSystemDetail from './pages/ATSGlobalSystemDetail';
import ATSEntitlementSystemDetail from './pages/ATSEntitlementSystemDetail';
import IFTComponentDetail from './pages/IFTComponentDetail';
import RVSSComponentDetail from './pages/RVSSComponentDetail';
import SensorIntegrationComponentDetail from './pages/SensorIntegrationComponentDetail';
import AlertResponseComponentDetail from './pages/AlertResponseComponentDetail';
import DevTechnologyContracts from './pages/DevTechnologyContracts';
import CargoSystemsContractDetail from './pages/CargoSystemsContractDetail';
import ACEATAPEnhancementContractDetail from './pages/ACEATAPEnhancementContractDetail';
import ACEATAPBridgeContractDetail from './pages/ACEATAPBridgeContractDetail';
import DHSICEContractsDetail from './pages/DHSICEContractsDetail';
import ApplicationDevelopmentCapability from './pages/ApplicationDevelopmentCapability';
import ModernizationDevSecOpsCapability from './pages/ModernizationDevSecOpsCapability';
import DataAnalyticsCapability from './pages/DataAnalyticsCapability';
import IdentityBiometricsCapability from './pages/IdentityBiometricsCapability';
import DataExchangeCatalog from './pages/DataExchangeCatalog';
import TimeLogCalendar from './pages/TimeLogCalendar';
import GapDashboard from './pages/gap-analysis/GapDashboard';
import Gitleaks from './pages/security/Gitleaks';
import DocumentationViewer from './pages/security/DocumentationViewer';
import TechStack from './pages/tech/TechStack';
import DevSecOpsPipeline from './pages/DevSecOpsPipeline';

// DHS Study Pages
import DHSStudyHome from './pages/dhs/DHSStudyHome';
import DHSComponents from './pages/dhs/DHSComponents';
import PolicyComponents from './pages/policy/PolicyComponents';
import DataAnalysisTopics from './pages/dhs/DataAnalysisTopics';
import AuthoritativeDataSources from './pages/dhs/AuthoritativeDataSources';
import FileDefinitions from './pages/dhs/FileDefinitions';
import SystemInventory from './pages/dhs/SystemInventory';
import MissionValue from './pages/dhs/MissionValue';
// DEV ONLY - DO NOT DEPLOY TO PRODUCTION
// import DHSInitiativeTracker from './pages/openSource/DHSInitiativeTracker';
import DockerInstructions from './pages/DockerInstructions';
import DockerK8sSupport from './pages/dev/DockerK8sSupport';

// ODOS-IV Imports
import ODOSDashboard from './pages/odos/ODOSDashboard';
import ODOSModuleDetail from './pages/odos/ODOSModuleDetail';
import ODOSActivities from './pages/odos/ODOSActivities';
import ODOSActivityForm from './pages/odos/ODOSActivityForm';
import ODOSPWSViewer from './pages/odos/ODOSPWSViewer';
import PWSViewer from './pages/PWSViewer';

// Import Auth Provider Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import DevTools from './components/DevTools/DevTools';

// Theme configuration has been moved to ThemeContext

/**
 * Async-loaded Product Backlog view 
 * @see {@link https://e-unify.ai/docs/dev-tools#productbacklog}
 */
const ProductBacklog = React.lazy(() => import('./pages/dev/ProductBacklog'));


/**
 * Async-loaded E-Unify Milestone Monitoring
 */
const EUnifyMilestoneMonitoring = React.lazy(() => import('./pages/dev/EUnifyMilestoneMonitoring'));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TEMPORARY: Bypass authentication for testing
  // In production, this would check user authentication status
  const { loading } = useAuth();
  
  // If authentication is loading, don't redirect yet
  if (loading) {
    return null;
  }
  
  // TEMPORARY: Always allow access for testing our accessible components
  // In production, this would redirect unauthenticated users to login
  
  // If user is logged in, render the children
  return <>{children}</>;
};

// Data Steward Protected Route
const DataStewardRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TEMPORARY: Bypass authentication for testing
  const { loading } = useAuth();
  
  // If authentication is loading, don't redirect yet
  if (loading) {
    return null;
  }
  
  // TEMPORARY: Always allow access for testing our accessible components
  // In production, this would verify the user is a data steward or admin
  
  // If user is authorized, render the children
  return <>{children}</>;
};

// Admin Protected Route
const AdminRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  // TEMPORARY: Bypass authentication for testing
  const { loading } = useAuth();
  
  // If authentication is loading, don't redirect yet
  if (loading) {
    return null;
  }
  
  // TEMPORARY: Always allow access for testing our accessible components
  // In production, this would verify the user is an admin
  
  // If user is authorized, render the children or nested routes
  return children ? <>{children}</> : <Outlet />;
};

// This simple test app doesn't require authentication context or any API calls
// It's used just to test our accessible components in isolation
const TestApp: React.FC = () => {
  return (
    <CustomThemeProvider>
      <CssBaseline />
      <TestPage />
    </CustomThemeProvider>
  );
};

function App() {
  // Check if we're in test mode via URL parameter
  const isTestMode = window.location.search.includes('testMode=true');
  
  // If test mode is enabled, render the simplified test app
  if (isTestMode) {
    return <TestApp />;
  }
  
  return (
    <CustomThemeProvider>
      <CssBaseline />
      <GlobalErrorBoundary>
        <AccessibilityProvider>
          <UiSettingsProvider>
            <AuthProvider>
              <SnackbarProvider>
                <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                {/* ScrollToTop component ensures the page starts at the top when navigating */}
                <EnvironmentIndicator />
                <ScrollToTop />
                {/* DevTools component for development only */}
                <Routes>
            {/* About Route - Authenticated with Dashboard Header (must be before public routes) */}
            <Route path="/about" element={<Layout><About /></Layout>} />
            
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/capabilities" element={<Capabilities />} />
            <Route path="/security" element={<SecurityCompliance />} />
            <Route path="/docs/view" element={<Layout><DocsViewer /></Layout>} />
            <Route path="/security/gitleaks" element={<Layout><AdminRoute><Gitleaks /></AdminRoute></Layout>} />
            <Route path="/security/docs" element={<Layout><AdminRoute><DocumentationViewer /></AdminRoute></Layout>} />
            <Route path="/devsecops" element={<Layout><ProtectedRoute><DevSecOpsPipeline /></ProtectedRoute></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/contact/thank-you" element={<Layout><ContactThankYou /></Layout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dev-login" element={<DevLogin />} />
            
            {/* Consolidated Dev Routes */}
            <Route path="/dev">
              {/* Temporarily disabled - using /dev/milestones instead */}
              {/* <Route path="product-backlog" element={
                <ProtectedLayout roles={['admin']}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <ProductBacklog />
                  </Suspense>
                </ProtectedLayout>
              } /> */}
              {/* DailyTasksView component not found - route disabled */}
              {/* <Route path="daily-tasks" element={
                <Suspense fallback={<div>Loading...</div>}>
                  <DailyTasksView />
                </Suspense>
              } /> */}
              <Route path="unidatum-pm-planning" element={<UnidatumProjectManagerPlanningV2 />} />
              <Route path="pdca-dashboard" element={<PDCADashboard />} />
              <Route path="milestones" element={
                <ProtectedLayout roles={['admin']}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <EUnifyMilestoneMonitoring />
                  </Suspense>
                </ProtectedLayout>
              } />
              <Route path="docker-instructions" element={<DockerInstructions />} />
              <Route path="docker-k8s-support" element={<DockerK8sSupport />} />
            </Route>
            
            {/* Feature Pages - Public */}
            <Route path="/features/data-catalog" element={<DataCatalogFeature />} />
            <Route path="/features/data-governance" element={<DataGovernanceFeature />} />
            <Route path="/features/analytics" element={<AnalyticsFeature />} />
            <Route path="/features/integration" element={<IntegrationFeature />} />
            
            {/* Core Features Detail Pages */}
            <Route path="/features/business-glossary" element={<BusinessGlossaryFeature />} />
            <Route path="/features/data-catalog-detail" element={<DataCatalogDetailFeature />} />
            <Route path="/features/data-lineage" element={<DataLineageFeature />} />
            <Route path="/features/policy-management" element={<PolicyManagementFeature />} />
            <Route path="/features/workflow-automation" element={<WorkflowAutomationFeature />} />
            <Route path="/features/impact-analysis" element={<ImpactAnalysisFeature />} />
            
            {/* Core Capabilities Detail Pages */}
            <Route path="/capabilities/high-performance" element={<HighPerformance />} />
            <Route path="/capabilities/zero-trust-security" element={<ZeroTrustSecurity />} />
            <Route path="/capabilities/fedramp-ready" element={<FedRAMPReady />} />
            <Route path="/capabilities/section-508-compliant" element={<Section508Compliant />} />
            <Route path="/capabilities/scalable-architecture" element={<ScalableArchitecture />} />
            <Route path="/capabilities/no-vendor-lock-in" element={<NoVendorLockIn />} />
            
            {/* Temporary Testing Route */}
            <Route path="/test/data-assets" element={<DataAssetDemo />} />
            
            {/* Routes with Layout - wrap each element individually */}
            <Route path="/learn101" element={<Layout><Learn101 /></Layout>} />
            <Route path="/data-literacy" element={<Layout><DataLiteracyModule /></Layout>} />
            <Route path="/national-production-dataset" element={<Layout><NationalProductionDataset /></Layout>} />
            <Route path="/e22-classification" element={<Layout><E22Classification isAdmin={true} /></Layout>} />
            <Route path="/dgvsmdm" element={<Layout><DGvsMDMLesson /></Layout>} />
            <Route path="/data-steward-lesson" element={<Layout><DataStewardLesson /></Layout>} />
            {/* /about route moved to top of Routes section */}
            <Route path="/data-strategy-support" element={<Layout><DataStrategySupport /></Layout>} />
            <Route path="/help" element={<Layout><Help /></Layout>} />
              
            {/* Protected Routes - Require Authentication */}
            <Route path="/dashboard" element={<Layout><ProtectedRoute><Dashboard /></ProtectedRoute></Layout>} />
            <Route path="/search" element={<Layout><ProtectedRoute><SearchResults /></ProtectedRoute></Layout>} />
            <Route path="/portfolio" element={<Layout><ProtectedRoute><Portfolio /></ProtectedRoute></Layout>} />
            <Route path="/profile" element={<Layout><ProtectedRoute><Profile /></ProtectedRoute></Layout>} />
            <Route path="/security-dashboard" element={<Layout><ProtectedRoute><SecurityDashboard /></ProtectedRoute></Layout>} />
            <Route path="/time-log" element={<Layout><ProtectedRoute><TimeLogCalendar /></ProtectedRoute></Layout>} />
            <Route path="/data-catalog" element={<Layout><ProtectedRoute><DataCatalog /></ProtectedRoute></Layout>} />
            <Route path="/data-assets/:id" element={<Layout><ProtectedRoute><DataAssetDetail /></ProtectedRoute></Layout>} />
            <Route path="/data-assets" element={<Layout><ProtectedRoute><DataAssetDemo /></ProtectedRoute></Layout>} />
            <Route path="/project-charter" element={<Layout><ProtectedRoute><ProjectCharter /></ProtectedRoute></Layout>} />
            <Route path="/templates/kpi-dictionary" element={<Layout><ProtectedRoute><KpiDictionary /></ProtectedRoute></Layout>} />
            <Route path="/templates/kpi-dictionary/:id" element={<Layout><ProtectedRoute><KpiDictionary /></ProtectedRoute></Layout>} />
            <Route path="/templates/kpis" element={<Layout><ProtectedRoute><KpisBrowser /></ProtectedRoute></Layout>} />
            <Route path="/assets/business-processes" element={<Layout><ProtectedRoute><BusinessProcesses /></ProtectedRoute></Layout>} />
            <Route path="/assets/data-categories" element={<Layout><ProtectedRoute><DataCategories /></ProtectedRoute></Layout>} />
            <Route path="/assets/data-concepts" element={<Layout><ProtectedRoute><DataConcepts /></ProtectedRoute></Layout>} />
            <Route path="/assets/data-domains" element={<Layout><ProtectedRoute><DataDomains /></ProtectedRoute></Layout>} />
            <Route path="/assets/subject-categories" element={<Layout><ProtectedRoute><SubjectCategories /></ProtectedRoute></Layout>} />
            <Route path="/assets/line-of-business" element={<Layout><ProtectedRoute><LineOfBusiness /></ProtectedRoute></Layout>} />
            <Route path="/context/business-terms" element={<Layout><ProtectedRoute><BusinessTerms /></ProtectedRoute></Layout>} />
            <Route path="/business-glossary" element={<Layout><ProtectedRoute><BusinessGlossary /></ProtectedRoute></Layout>} />
            <Route path="/business-glossary/catalog" element={<Layout><ProtectedRoute><BusinessGlossaryCatalog /></ProtectedRoute></Layout>} />
            <Route path="/business-glossary/training" element={<Layout><ProtectedRoute><BusinessGlossaryTraining /></ProtectedRoute></Layout>} />
            <Route path="/business-glossary/training-v2" element={<Layout><ProtectedRoute><BusinessGlossaryTrainingV2 /></ProtectedRoute></Layout>} />
            <Route path="/data-intelligence/training" element={<Layout><ProtectedRoute><DataIntelligenceTraining /></ProtectedRoute></Layout>} />
            <Route path="/data-literacy/training" element={<Layout><ProtectedRoute><DataLiteracyTraining /></ProtectedRoute></Layout>} />
            <Route path="/data-governance/training" element={<Layout><ProtectedRoute><DataGovernanceTraining /></ProtectedRoute></Layout>} />
            <Route path="/data-catalog/training" element={<Layout><ProtectedRoute><DataCatalogTraining /></ProtectedRoute></Layout>} />
            <Route path="/data-quality-lineage/training" element={<Layout><ProtectedRoute><DataQualityLineageTraining /></ProtectedRoute></Layout>} />
            <Route path="/vanguard" element={<Layout><ProtectedRoute><VanguardCertification /></ProtectedRoute></Layout>} />
            <Route path="/certification/vanguard" element={<Layout><ProtectedRoute><VanguardCertification /></ProtectedRoute></Layout>} />
            <Route path="/context/acronyms" element={<Layout><ProtectedRoute><Acronyms /></ProtectedRoute></Layout>} />
            <Route path="/context/kpis" element={<Layout><Navigate to="/templates/kpis" replace /></Layout>} />
            <Route path="/context/reports" element={<Layout><ProtectedRoute><Reports /></ProtectedRoute></Layout>} />
            <Route path="/context/bi-reports" element={<Layout><ProtectedRoute><BIReports /></ProtectedRoute></Layout>} />
            <Route path="/analytics" element={<Layout><ProtectedRoute><Analytics /></ProtectedRoute></Layout>} />
            <Route path="/integration" element={<Layout><ProtectedRoute><Integration /></ProtectedRoute></Layout>} />
            <Route path="/tech-stack" element={<Layout><ProtectedRoute><TechStack /></ProtectedRoute></Layout>} />
            <Route path="/study-aids/business-analytics" element={<Layout><ProtectedRoute><StudyAidsBusinessAnalytics /></ProtectedRoute></Layout>} />
            <Route path="/asset-types" element={<Layout><ProtectedRoute><AssetTypes /></ProtectedRoute></Layout>} />
            <Route path="/testpage" element={<Layout><ProtectedRoute><TestPage /></ProtectedRoute></Layout>} />
            {/* DHS Study Routes */}
            <Route path="/dhs" element={<Layout><ProtectedRoute><DHSStudyHome /></ProtectedRoute></Layout>} />
            <Route path="/dhs/components" element={<Layout><ProtectedRoute><DHSComponents /></ProtectedRoute></Layout>} />
            <Route path="/dhs/data-topics" element={<Layout><ProtectedRoute><DataAnalysisTopics /></ProtectedRoute></Layout>} />
            <Route path="/dhs/data-sources" element={<Layout><ProtectedRoute><AuthoritativeDataSources /></ProtectedRoute></Layout>} />
            <Route path="/dhs/file-definitions" element={<Layout><ProtectedRoute><FileDefinitions /></ProtectedRoute></Layout>} />
            <Route path="/dhs/systems" element={<Layout><ProtectedRoute><SystemInventory /></ProtectedRoute></Layout>} />
            <Route path="/dhs/value" element={<Layout><ProtectedRoute><MissionValue /></ProtectedRoute></Layout>} />
            {/* DEV ONLY - DO NOT DEPLOY TO PRODUCTION */}
            {/* <Route path="/dhs/initiative-tracker" element={<Layout><ProtectedRoute><DHSInitiativeTracker /></ProtectedRoute></Layout>} /> */}
            {/* Documentation routes - Combined view with both old and new content */}
            <Route path="/docs" element={<Layout><ProtectedRoute><DocumentationCombined /></ProtectedRoute></Layout>} />
            <Route path="/documentation" element={<Layout><ProtectedRoute><DocumentationCombined /></ProtectedRoute></Layout>} />
            <Route path="/settings" element={<Layout><ProtectedRoute><Settings /></ProtectedRoute></Layout>} />
            <Route path="/processing/categories" element={<Layout><ProtectedRoute><ProcessingCategories /></ProtectedRoute></Layout>} />
            <Route path="/processing/measures" element={<Layout><ProtectedRoute><ProcessingMeasures /></ProtectedRoute></Layout>} />
            <Route path="/processing/migration" element={<Layout><ProtectedRoute><ProcessingMigration /></ProtectedRoute></Layout>} />
            <Route path="/policy" element={<Layout><ProtectedRoute><PolicyIndex /></ProtectedRoute></Layout>} />
            <Route path="/policy/gdpr" element={<Layout><ProtectedRoute><GDPRPolicy /></ProtectedRoute></Layout>} />
            <Route path="/policy/standards" element={<Layout><ProtectedRoute><Standards /></ProtectedRoute></Layout>} />
            <Route path="/standards" element={<Layout><ProtectedRoute><StandardsLibrary /></ProtectedRoute></Layout>} />
            <Route path="/weekly-status" element={<Layout><ProtectedRoute><WeeklyStatusList /></ProtectedRoute></Layout>} />
            <Route path="/monthly-status" element={<Layout><ProtectedRoute><MonthlyStatusList /></ProtectedRoute></Layout>} />
            <Route path="/data-governance-quality" element={<Layout><ProtectedRoute><DataGovernanceQuality /></ProtectedRoute></Layout>} />
            <Route path="/data-quality" element={<Layout><ProtectedRoute><DataQuality /></ProtectedRoute></Layout>} />
            <Route path="/compliance" element={<Layout><ProtectedRoute><Compliance /></ProtectedRoute></Layout>} />
            <Route path="/tasks" element={<Layout><ProtectedRoute><OpenTasks /></ProtectedRoute></Layout>} />
            <Route path="/federal-data-strategy" element={<Layout><ProtectedRoute><FederalDataStrategy /></ProtectedRoute></Layout>} />
            <Route path="/benefits" element={<Layout><ProtectedRoute><BenefitsPage /></ProtectedRoute></Layout>} />
            <Route path="/benefits/:benefitId" element={<Layout><ProtectedRoute><BenefitDetail /></ProtectedRoute></Layout>} />
                
            {/* ODOS-IV Routes */}
            <Route path="/odos" element={<Layout><ProtectedRoute><ODOSDashboard /></ProtectedRoute></Layout>} />
            <Route path="/odos/pws" element={<Layout><ProtectedRoute><ODOSPWSViewer /></ProtectedRoute></Layout>} />
            <Route path="/odos/modules/:id" element={<Layout><ProtectedRoute><ODOSModuleDetail /></ProtectedRoute></Layout>} />
            <Route path="/odos/modules/:id/activities" element={<Layout><ProtectedRoute><ODOSActivities /></ProtectedRoute></Layout>} />
            <Route path="/odos/activities" element={<Layout><ProtectedRoute><ODOSActivities /></ProtectedRoute></Layout>} />
            <Route path="/odos/activities/:id" element={<Layout><ProtectedRoute><ODOSActivityForm /></ProtectedRoute></Layout>} />

            {/* PWS Content Routes */}
            <Route path="/pws" element={<Layout><ProtectedRoute><PWSViewer /></ProtectedRoute></Layout>} />
            <Route path="/pws/:section" element={<Layout><ProtectedRoute><PWSViewer /></ProtectedRoute></Layout>} />
            <Route path="/pws/:section/:page" element={<Layout><ProtectedRoute><PWSViewer /></ProtectedRoute></Layout>} />
                
            {/* Data Governance Routes - User Access */}
            <Route path="/governance/dashboard" element={<Layout><ProtectedRoute><GovernanceDashboard /></ProtectedRoute></Layout>} />
            <Route path="/governance/policies" element={<Layout><ProtectedRoute><PoliciesStandards /></ProtectedRoute></Layout>} />
            <Route path="/governance/helpdesk" element={<Layout><ProtectedRoute><GovernanceHelpdesk /></ProtectedRoute></Layout>} />
                
            {/* Data Strategy Routes - User Access */}
            <Route path="/strategy/vision" element={<Layout><ProtectedRoute><VisionMission /></ProtectedRoute></Layout>} />
            <Route path="/strategy/training" element={<Layout><ProtectedRoute><TrainingResources /></ProtectedRoute></Layout>} />
              
            {/* Data Steward Only Routes */}
            <Route path="/data-governance" element={<Layout><DataStewardRoute><DataGovernance /></DataStewardRoute></Layout>} />
                
            {/* Data Governance - Data Steward Access */}
            <Route path="/governance/council" element={<Layout><DataStewardRoute><DataGovernanceCouncil /></DataStewardRoute></Layout>} />
            <Route path="/governance/quality" element={<Layout><DataStewardRoute><DataQualityMgmt /></DataStewardRoute></Layout>} />
            <Route path="/governance/compliance" element={<Layout><DataStewardRoute><ComplianceAssessments /></DataStewardRoute></Layout>} />
                
            {/* Data Strategy - Data Steward Access */}
            <Route path="/strategy/roadmap" element={<Layout><DataStewardRoute><StrategicRoadmap /></DataStewardRoute></Layout>} />
            <Route path="/strategy/maturity" element={<Layout><DataStewardRoute><MaturityAssessment /></DataStewardRoute></Layout>} />
              
            {/* Data Steward Routes - Direct access for testing */}
            <Route path="/data-steward" element={<Layout><DataStewardCenter /></Layout>} />

            {/* Admin Only Routes */}
            <Route path="/admin" element={<Layout><AdminRoute><Outlet /></AdminRoute></Layout>}>
              <Route path="/admin/dashboard" element={<Dashboard />} />
              <Route path="/admin/policy-manager" element={<PolicyManager />} />
              <Route path="/admin/domain-types" element={<DomainTypes />} />
              <Route path="/admin/statuses" element={<Statuses />} />
              <Route path="/admin/characteristics" element={<Characteristics />} />
              <Route path="/admin/dataQuality" element={<Dashboard />} />
              <Route path="/admin/dataStewards" element={<Dashboard />} />
              <Route path="/admin/workflows" element={<Workflows />} />
              <Route path="/admin/menu-management" element={<AdminMenuManagement />} />
              <Route path="/admin/contact-inquiries" element={<ContactInquiriesReport />} />
              <Route path="/admin/users" element={<AdminUserManagement />} />
              <Route path="/admin/data-strategy-planning" element={<DataStrategyPlanning />} />
              <Route path="/admin/data-strategy-modules" element={<DataStrategyModules />} />
              <Route path="/admin/data-strategy-modules/:moduleNumber" element={<DataStrategyModuleDetail />} />
              <Route path="/admin/pi-planning" element={<PIPlanningBoard />} />
              <Route path="/admin/ats-system" element={<ATSSystemDetail />} />
              <Route path="/admin/ift-rvss-system" element={<IFTRVSSSystemDetail />} />
              <Route path="/admin/eid-system" element={<EIDSystemDetail />} />
              <Route path="/admin/uip-system" element={<UIPSystemDetail />} />
              <Route path="/admin/ace-system" element={<ACESystemDetail />} />
              <Route path="/admin/abi-system" element={<ABISystemDetail />} />
              <Route path="/admin/csms-system" element={<CSMSSystemDetail />} />
              <Route path="/admin/aap-system" element={<AAPSystemDetail />} />
              <Route path="/admin/ai-llm-system" element={<AILLMSystemDetail />} />
              <Route path="/admin/cloud-edge-system" element={<CloudEdgeSystemDetail />} />
              <Route path="/admin/oit-portal-system" element={<OITPortalSystemDetail />} />
              <Route path="/admin/innovation-hub-system" element={<InnovationHubSystemDetail />} />
              <Route path="/admin/ats-cargo-system" element={<ATSCargoSystemDetail />} />
              <Route path="/admin/ats-passenger-system" element={<ATSPassengerSystemDetail />} />
              <Route path="/admin/ats-land-system" element={<ATSLandSystemDetail />} />
              <Route path="/admin/ats-international-system" element={<ATSInternationalSystemDetail />} />
              <Route path="/admin/ats-global-system" element={<ATSGlobalSystemDetail />} />
              <Route path="/admin/ats-entitlement-system" element={<ATSEntitlementSystemDetail />} />
              <Route path="/admin/ift-component" element={<IFTComponentDetail />} />
              <Route path="/admin/rvss-component" element={<RVSSComponentDetail />} />
              <Route path="/admin/sensor-integration-component" element={<SensorIntegrationComponentDetail />} />
              <Route path="/admin/alert-response-component" element={<AlertResponseComponentDetail />} />
              <Route path="/admin/dev-technology-contracts" element={<DevTechnologyContracts />} />
              <Route path="/admin/cargo-systems-contract" element={<CargoSystemsContractDetail />} />
              <Route path="/admin/ace-atap-enhancement-contract" element={<ACEATAPEnhancementContractDetail />} />
              <Route path="/admin/ace-atap-bridge-contract" element={<ACEATAPBridgeContractDetail />} />
              <Route path="/admin/dhs-ice-contracts" element={<DHSICEContractsDetail />} />
              <Route path="/admin/application-development-capability" element={<ApplicationDevelopmentCapability />} />
              <Route path="/admin/modernization-devsecops-capability" element={<ModernizationDevSecOpsCapability />} />
              <Route path="/admin/data-analytics-capability" element={<DataAnalyticsCapability />} />
              <Route path="/admin/identity-biometrics-capability" element={<IdentityBiometricsCapability />} />
              <Route path="/admin/data-exchange-catalog" element={<DataExchangeCatalog />} />
              <Route path="/admin/data-catalog-roadmap" element={<DataCatalogRoadmap />} />
              <Route path="/admin/ai-governance-roadmap" element={<AIGovernanceRoadmap />} />
              <Route path="/admin/portfolio-management-roadmap" element={<PortfolioManagementRoadmap />} />
              <Route path="/admin/npd-roadmap" element={<NPDRoadmap />} />
              <Route path="/admin/uscis-application-tracking" element={<USCISApplicationTracking />} />
              <Route path="/admin/data-strategy-operations-center" element={<DataStrategyOperationsCenter />} />
              <Route path="/admin/project-details" element={<ProjectDetails />} />
              <Route path="/admin/team-utilization-details" element={<TeamUtilizationDetails />} />
              <Route path="/admin/compliance-details" element={<ComplianceDetails />} />
              <Route path="/admin/automated-processes" element={<AutomatedProcesses />} />
              <Route path="/admin/scheduled-processes" element={<ScheduledProcessesPage />} />
              <Route path="/admin/process-monitoring" element={<ProcessMonitoring />} />
              <Route path="/admin/team-roster" element={<TeamRoster />} />
              <Route path="/admin/team-roster-picklists" element={<TeamRosterPicklistManagement />} />
              <Route path="/admin/janusgraph" element={<JanusGraphVisualization />} />
              <Route path="/admin/documentation" element={<DocumentationCenter />} />
              <Route path="/admin/capstone" element={<CapstoneProjects />} />
              <Route path="/admin/capstone/projects/:projectId" element={<CapstoneProjectDetail />} />
              <Route path="/admin/case-studies/maf" element={<CaseStudyMAF />} />
              <Route path="/admin/data-inventory-timeline" element={<DataInventoryTimeline />} />
            </Route>

            {/* Routes outside AdminRoute protection */}
            <Route path="/data-quality" element={<Layout><DataQuality /></Layout>} />
            <Route path="/data-lineage" element={<Layout><DataLineage /></Layout>} />
            <Route path="/access" element={<Layout><Outlet /></Layout>}>
              <Route path="user-management" element={<UserManagement />} />
              <Route path="roles" element={<Roles />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="jurisdictions" element={<Jurisdictions />} />
            </Route>
              
            {/* Error Routes */}
            <Route path="/unauthorized" element={
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h1>Unauthorized Access</h1>
                <p>You don't have permission to access this resource.</p>
              </div>
            } />
            <Route path="*" element={
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h1>Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
              </div>
            } />
            <Route 
              path="/gap-analysis" 
              element={
                <Layout>
                  <ProtectedRoute>
                    <GapDashboard />
                  </ProtectedRoute>
                </Layout>
              } 
            />
          </Routes>
          {/* Development tools */}
          <DevTools />
        </BrowserRouter>
              </SnackbarProvider>
            </AuthProvider>
          </UiSettingsProvider>
        </AccessibilityProvider>
      </GlobalErrorBoundary>
    </CustomThemeProvider>
  );
}

export default App;
